export type ColumnKey =
  | "count"
  | "technology_type"
  | "production_month"
  | "status"
  | "portfolio_name"
  | "portfolio_type"
  | "registration_authority"
  | "registry_region"
  | "issuing_region"
  | "production_device_name"
  | "production_device_code"
  | "production_support"
  | "ecolabel_tags"
  | "transfer_deadline"
  | "sale_price"
  | "cod_age"
  | "limit_price"
  | "account_emails"
  | "registry_id"
  | "bundle_start"
  | "bundle_end";

/** Columns whose filters accept sets of literal values. */
export type SelectableFilterColumn =
  | "technology_type"
  | "status"
  | "portfolio_name"
  | "portfolio_type"
  | "production_device_name"
  | "production_device_code"
  | "production_support"
  | "ecolabel_tags"
  | "transfer_deadline"
  | "production_month"
  | "registration_authority"
  | "registry_region"
  | "issuing_region";

/** The columns naming a certificate's production device; they render and filter alike, with a device-group picker. */
export const DEVICE_COLUMNS = [
  "production_device_name",
  "production_device_code",
] as const satisfies readonly SelectableFilterColumn[];
export type DeviceColumn = (typeof DEVICE_COLUMNS)[number];

export type CodAgeOp = "LT" | "LE" | "EQ" | "GE" | "GT";
export type CodAgeCond = { op: CodAgeOp; value: number | null };
/** A condition with a year picked; one without a year doesn't filter. */
export type ActiveCodAgeCond = { op: CodAgeOp; value: number };
export type CodAgeFilterValue = { a: CodAgeCond; b: CodAgeCond | null };
export type ProgrammaticFilterColumn = SelectableFilterColumn | "cod_age";

export const COD_AGE_OPERATORS: Record<CodAgeOp, string> = {
  LT: "<",
  LE: "≤",
  EQ: "=",
  GE: "≥",
  GT: ">",
};

// What each operator means, for the matcher; COD_AGE_OPERATORS is what the editor and chips show.
const COD_AGE_TESTS: Record<
  CodAgeOp,
  (age: number, threshold: number) => boolean
> = {
  LT: (age, threshold) => age < threshold,
  LE: (age, threshold) => age <= threshold,
  EQ: (age, threshold) => age === threshold,
  GE: (age, threshold) => age >= threshold,
  GT: (age, threshold) => age > threshold,
};

/** Reset the COD filter without losing the shape required by its editor. */
export const emptyCodAgeFilter = (): CodAgeFilterValue => ({
  a: { op: "LE", value: null },
  b: null,
});

export const activeCodAgeConditions = (
  filter: CodAgeFilterValue
): ActiveCodAgeCond[] =>
  [filter.a, filter.b].filter(
    (condition): condition is ActiveCodAgeCond => condition?.value != null
  );

/**
 * Whether a row's COD age passes every active condition of the filter. A filter with no active
 * condition matches every row; a row without a COD age fails any active condition.
 */
export const matchesCodAge = (
  age: number | null | undefined,
  filter: CodAgeFilterValue
): boolean => {
  const conditions = activeCodAgeConditions(filter);
  if (conditions.length === 0) return true;
  if (age == null) return false;
  return conditions.every(({ op, value }) => COD_AGE_TESTS[op](age, value));
};

/**
 * Strict guard for untrusted input such as a stored URL: both conditions present (`b` may be
 * `null`), each with a known operator and a finite or unset year.
 */
export const isCodAgeFilterValue = (
  value: unknown
): value is CodAgeFilterValue =>
  typeof value === "object" &&
  value !== null &&
  "a" in value &&
  "b" in value &&
  isCodAgeCond(value.a) &&
  (value.b === null || isCodAgeCond(value.b));

const isCodAgeCond = (value: unknown): value is CodAgeCond =>
  typeof value === "object" &&
  value !== null &&
  "op" in value &&
  "value" in value &&
  typeof value.op === "string" &&
  Object.hasOwn(COD_AGE_OPERATORS, value.op) &&
  (value.value === null ||
    (typeof value.value === "number" && Number.isFinite(value.value)));

export interface ColumnDefinition {
  field: ColumnKey;
  header: string;
  width: { compact: string; default: string };
}

type ColumnProperties = Omit<ColumnDefinition, "field">;

const COLUMN_DEFINITIONS: Record<ColumnKey, ColumnProperties> = {
  // Wide enough for the filtered-total pill and the partial-amount editor.
  count: {
    header: "charts.fields.count",
    width: { compact: "5.75rem", default: "6rem" },
  },
  technology_type: {
    header: "charts.fields.technology_type",
    width: { compact: "6rem", default: "7rem" },
  },
  production_month: {
    header: "charts.fields.production_month",
    width: { compact: "5.5rem", default: "6rem" },
  },
  status: {
    header: "charts.fields.status",
    width: { compact: "6.5rem", default: "7rem" },
  },
  portfolio_name: {
    header: "charts.fields.portfolio_name",
    width: { compact: "9rem", default: "12rem" },
  },
  registration_authority: {
    header: "charts.fields.registration_authority",
    width: { compact: "6rem", default: "7rem" },
  },
  registry_region: {
    header: "charts.fields.registry_region",
    width: { compact: "7rem", default: "10rem" },
  },
  issuing_region: {
    header: "charts.fields.issuing_region",
    width: { compact: "7rem", default: "10rem" },
  },
  production_device_name: {
    header: "charts.fields.production_device_name",
    width: { compact: "10rem", default: "14rem" },
  },
  production_device_code: {
    header: "charts.fields.production_device_code",
    width: { compact: "9rem", default: "11rem" },
  },
  production_support: {
    header: "charts.fields.production_support",
    width: { compact: "6rem", default: "6.5rem" },
  },
  ecolabel_tags: {
    header: "charts.fields.ecolabel_tags",
    width: { compact: "7rem", default: "8rem" },
  },
  transfer_deadline: {
    header: "charts.fields.transfer_deadline",
    width: { compact: "5.5rem", default: "6rem" },
  },
  sale_price: {
    header: "charts.fields.sale_price",
    width: { compact: "5.5rem", default: "6rem" },
  },
  portfolio_type: {
    header: "charts.fields.portfolio_type",
    width: { compact: "7rem", default: "8rem" },
  },
  cod_age: {
    header: "charts.fields.cod_age",
    width: { compact: "8.5rem", default: "9rem" },
  },
  limit_price: {
    header: "charts.fields.limit_price",
    width: { compact: "5.5rem", default: "6rem" },
  },
  account_emails: {
    header: "charts.fields.account_emails",
    width: { compact: "10rem", default: "12rem" },
  },
  registry_id: {
    header: "charts.fields.registry_id",
    width: { compact: "8rem", default: "10rem" },
  },
  bundle_start: {
    header: "charts.fields.bundle_start",
    width: { compact: "7rem", default: "8rem" },
  },
  bundle_end: {
    header: "charts.fields.bundle_end",
    width: { compact: "7rem", default: "8rem" },
  },
};

export const ALL_COLUMNS = Object.fromEntries(
  Object.entries(COLUMN_DEFINITIONS).map(([key, props]) => [
    key,
    { field: key as ColumnKey, ...props },
  ])
) as Record<ColumnKey, ColumnDefinition>;

// Default columns for main portfolio overview
export const DEFAULT_COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "status",
  "portfolio_name",
  "production_device_name",
  "production_support",
  "transfer_deadline",
  "sale_price",
];

// For Cancel/Sell/Send - show registry_region and portfolio_name, hide status
export const TRANSACTION_COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "portfolio_name",
  "registry_region",
  "production_device_name",
  "transfer_deadline",
];

export const ADMIN_TRANSACTION_COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "portfolio_type",
  "portfolio_name",
  "account_emails",
  "registration_authority",
  "registry_region",
  "issuing_region",
  "production_device_name",
  "production_support",
  "ecolabel_tags",
  "cod_age",
  "limit_price",
  "transfer_deadline",
  "registry_id",
  "bundle_start",
  "bundle_end",
];

// Soldera Inventory: the only columns a buyer may see of Soldera's sellable pool. An allowlist, so a
// new column stays hidden there until someone decides a buyer may see it.
export const SOLDERA_INVENTORY_COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "registration_authority",
  "registry_region",
  "issuing_region",
  "production_support",
  "ecolabel_tags",
  "cod_age",
  "transfer_deadline",
];

// For PortfolioCard expanded view - show registry_region instead of portfolio_name
export const PORTFOLIO_CARD_COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "status",
  "registry_region",
  "production_device_name",
  "production_support",
  "transfer_deadline",
];
